/** Branded ID types. All IDs are UUIDv7 strings: time-ordered, safe as outbox keys. */
declare const brand: unique symbol
type Brand<T, B> = T & { readonly [brand]: B }

export type PersonId = Brand<string, 'PersonId'>
export type MemberId = Brand<string, 'MemberId'>
export type GuestId = Brand<string, 'GuestId'>
export type ResourceId = Brand<string, 'ResourceId'>
export type BookingId = Brand<string, 'BookingId'>
export type QueueEntryId = Brand<string, 'QueueEntryId'>
export type MatchId = Brand<string, 'MatchId'>
export type ChoreDefId = Brand<string, 'ChoreDefId'>
export type ChoreInstanceId = Brand<string, 'ChoreInstanceId'>
export type SwapId = Brand<string, 'SwapId'>
export type ProposalId = Brand<string, 'ProposalId'>
export type BillId = Brand<string, 'BillId'>
export type BillInstanceId = Brand<string, 'BillInstanceId'>
export type PaymentId = Brand<string, 'PaymentId'>
export type ExpenseId = Brand<string, 'ExpenseId'>
export type SettlementId = Brand<string, 'SettlementId'>
export type FoodOrderId = Brand<string, 'FoodOrderId'>
export type SupplyItemId = Brand<string, 'SupplyItemId'>
export type NoteId = Brand<string, 'NoteId'>
export type TaskId = Brand<string, 'TaskId'>
export type WikiPageId = Brand<string, 'WikiPageId'>
export type ChecklistId = Brand<string, 'ChecklistId'>
export type ChecklistRunId = Brand<string, 'ChecklistRunId'>
export type PlanId = Brand<string, 'PlanId'>
export type NodeId = Brand<string, 'NodeId'>
export type WallId = Brand<string, 'WallId'>
export type OpeningId = Brand<string, 'OpeningId'>
export type FurnitureId = Brand<string, 'FurnitureId'>
export type RoomId = Brand<string, 'RoomId'>
export type WifiSourceId = Brand<string, 'WifiSourceId'>
export type MutationId = Brand<string, 'MutationId'>
export type DeviceId = Brand<string, 'DeviceId'>

/** UUIDv7: 48-bit big-endian ms timestamp, version 7, 74 random bits. */
export function uuidv7(nowMs: number, rand: () => number = Math.random): string {
  const ts = Math.floor(nowMs)
  const bytes = new Uint8Array(16)
  bytes[0] = (ts / 2 ** 40) & 0xff
  bytes[1] = (ts / 2 ** 32) & 0xff
  bytes[2] = (ts / 2 ** 24) & 0xff
  bytes[3] = (ts / 2 ** 16) & 0xff
  bytes[4] = (ts / 2 ** 8) & 0xff
  bytes[5] = ts & 0xff
  for (let i = 6; i < 16; i++) bytes[i] = Math.floor(rand() * 256) & 0xff
  bytes[6] = (bytes[6]! & 0x0f) | 0x70 // version 7
  bytes[8] = (bytes[8]! & 0x3f) | 0x80 // variant 10
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/
export function isUuidv7(s: string): boolean { return UUID_RE.test(s) }

/** Extract the embedded millisecond timestamp from a UUIDv7. */
export function uuidv7Timestamp(id: string): number {
  const hex = id.replace(/-/g, '').slice(0, 12)
  return Number.parseInt(hex, 16)
}

export const asId = <T extends string>(s: string): T => s as T
